import React, { useState, useEffect } from 'react';
import { 
    testEndpoint, 
    saveEndpoint, 
    getEnvironments,
    saveRequestHistory,
    searchEndpoints 
} from '../services/api';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';

// Helper function for method colors
function getMethodColor(method) {
    const colors = {
        GET: 'text-green-600',
        POST: 'text-blue-600',
        PUT: 'text-orange-600',
        PATCH: 'text-yellow-600',
        DELETE: 'text-red-600',
        HEAD: 'text-purple-600',
        OPTIONS: 'text-gray-600'
    };
    return colors[method] || 'text-gray-600';
}

// KeyValueEditor Component
function KeyValueEditor({ items, onChange, placeholder }) {
    const handleAdd = () => {
        onChange([...items, { key: '', value: '', enabled: true }]);
    };
    
    const handleUpdate = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;
        onChange(updated);
    };
    
    const handleRemove = (index) => {
        onChange(items.filter((_, i) => i !== index));
    };
    
    return (
        <div className="space-y-2">
            {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => handleUpdate(index, 'enabled', e.target.checked)}
                    />
                    <input
                        type="text"
                        value={item.key}
                        onChange={(e) => handleUpdate(index, 'key', e.target.value)}
                        placeholder={placeholder.key}
                        className="flex-1 px-2 py-1 border rounded"
                    />
                    <input
                        type="text"
                        value={item.value}
                        onChange={(e) => handleUpdate(index, 'value', e.target.value)}
                        placeholder={placeholder.value}
                        className="flex-1 px-2 py-1 border rounded"
                    />
                    <button
                        onClick={() => handleRemove(index)}
                        className="text-red-600 hover:text-red-800"
                    >
                        Remove
                    </button>
                </div>
            ))}
            <button
                onClick={handleAdd}
                className="text-blue-600 hover:text-blue-800 text-sm"
            >
                + Add {placeholder.key}
            </button>
        </div>
    );
}

// AuthorizationEditor Component
function AuthorizationEditor({ auth, onChange }) {
    const authTypes = [
        { value: 'none', label: 'No Auth' },
        { value: 'bearer', label: 'Bearer Token' },
        { value: 'basic', label: 'Basic Auth' },
        { value: 'apikey', label: 'API Key' }
    ];
    
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                </label>
                <select
                    value={auth.type}
                    onChange={(e) => onChange({ ...auth, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                >
                    {authTypes.map(type => (
                        <option key={type.value} value={type.value}>
                            {type.label}
                        </option>
                    ))}
                </select>
            </div>
            
            {auth.type === 'bearer' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Token
                    </label>
                    <input
                        type="text"
                        value={auth.bearer?.token || ''}
                        onChange={(e) => onChange({
                            ...auth,
                            bearer: { token: e.target.value }
                        })}
                        placeholder="Enter bearer token"
                        className="w-full px-3 py-2 border rounded"
                    />
                </div>
            )}
            
            {auth.type === 'basic' && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Username
                        </label>
                        <input
                            type="text"
                            value={auth.basic?.username || ''}
                            onChange={(e) => onChange({
                                ...auth,
                                basic: { ...auth.basic, username: e.target.value }
                            })}
                            placeholder="Username"
                            className="w-full px-3 py-2 border rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            value={auth.basic?.password || ''}
                            onChange={(e) => onChange({
                                ...auth,
                                basic: { ...auth.basic, password: e.target.value }
                            })}
                            placeholder="Password"
                            className="w-full px-3 py-2 border rounded"
                        />
                    </div>
                </div>
            )}
            
            {auth.type === 'apikey' && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key
                        </label>
                        <input
                            type="text"
                            value={auth.apikey?.key || ''}
                            onChange={(e) => onChange({
                                ...auth,
                                apikey: { ...auth.apikey, key: e.target.value }
                            })}
                            placeholder="Key"
                            className="w-full px-3 py-2 border rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Value
                        </label>
                        <input
                            type="text"
                            value={auth.apikey?.value || ''}
                            onChange={(e) => onChange({
                                ...auth,
                                apikey: { ...auth.apikey, value: e.target.value }
                            })}
                            placeholder="Value"
                            className="w-full px-3 py-2 border rounded"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add to
                        </label>
                        <select
                            value={auth.apikey?.in || 'header'}
                            onChange={(e) => onChange({
                                ...auth,
                                apikey: { ...auth.apikey, in: e.target.value }
                            })}
                            className="w-full px-3 py-2 border rounded"
                        >
                            <option value="header">Header</option>
                            <option value="query">Query Params</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
}

// BodyEditor Component
function BodyEditor({ body, onChange }) {
    const bodyModes = [
        { value: 'none', label: 'none' },
        { value: 'raw', label: 'raw' },
        { value: 'formdata', label: 'form-data' },
        { value: 'urlencoded', label: 'x-www-form-urlencoded' }
    ];
    
    return (
        <div className="space-y-4">
            <div>
                <select
                    value={body.mode || 'raw'}
                    onChange={(e) => onChange({ ...body, mode: e.target.value })}
                    className="px-3 py-2 border rounded"
                >
                    {bodyModes.map(mode => (
                        <option key={mode.value} value={mode.value}>
                            {mode.label}
                        </option>
                    ))}
                </select>
            </div>
            
            {body.mode === 'raw' && (
                <div>
                    <CodeMirror
                        value={body.raw || ''}
                        onChange={(value) => onChange({ ...body, raw: value })}
                        extensions={[json()]}
                        theme="light"
                        height="400px"
                        placeholder="Enter request body..."
                    />
                </div>
            )}
            
            {body.mode === 'formdata' && (
                <KeyValueEditor
                    items={body.formdata || []}
                    onChange={(formdata) => onChange({ ...body, formdata })}
                    placeholder={{ key: 'Key', value: 'Value' }}
                />
            )}
            
            {body.mode === 'urlencoded' && (
                <KeyValueEditor
                    items={body.urlencoded || []}
                    onChange={(urlencoded) => onChange({ ...body, urlencoded })}
                    placeholder={{ key: 'Key', value: 'Value' }}
                />
            )}
        </div>
    );
}

// ParamsEditor Component
function ParamsEditor({ params, onChange }) {
    return (
        <KeyValueEditor
            items={params}
            onChange={onChange}
            placeholder={{ key: 'Key', value: 'Value' }}
        />
    );
}

// HeadersEditor Component
function HeadersEditor({ headers, onChange }) {
    return (
        <KeyValueEditor
            items={headers}
            onChange={onChange}
            placeholder={{ key: 'Header', value: 'Value' }}
        />
    );
}

// Request Builder Component
function RequestBuilder({ endpoint, onChange, onSend, loading }) {
    const [activeSection, setActiveSection] = useState('params');
    
    return (
        <div className="flex-1 flex flex-col bg-white">
            {/* URL Bar */}
            <div className="p-4 border-b">
                <div className="flex gap-2">
                    <select
                        value={endpoint.method}
                        onChange={(e) => onChange({ ...endpoint, method: e.target.value })}
                        className="px-3 py-2 border rounded"
                    >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                        <option value="HEAD">HEAD</option>
                        <option value="OPTIONS">OPTIONS</option>
                    </select>
                    
                    <input
                        type="text"
                        value={endpoint.url}
                        onChange={(e) => onChange({ ...endpoint, url: e.target.value })}
                        placeholder="Enter request URL"
                        className="flex-1 px-3 py-2 border rounded"
                    />
                    
                    <button
                        onClick={onSend}
                        disabled={loading || !endpoint.url}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {loading ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </div>
            
            {/* Request Sections */}
            <div className="flex-1 flex flex-col">
                {/* Section Tabs */}
                <div className="border-b">
                    <nav className="flex">
                        {['params', 'authorization', 'headers', 'body', 'pre-request', 'tests'].map(section => (
                            <button
                                key={section}
                                onClick={() => setActiveSection(section)}
                                className={`px-4 py-2 text-sm font-medium capitalize border-b-2 ${
                                    activeSection === section
                                        ? 'text-blue-600 border-blue-600'
                                        : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                                {section === 'pre-request' ? 'Pre-request Script' : section}
                            </button>
                        ))}
                    </nav>
                </div>
                
                {/* Section Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {activeSection === 'params' && (
                        <ParamsEditor
                            params={endpoint.params}
                            onChange={(params) => onChange({ ...endpoint, params })}
                        />
                    )}
                    
                    {activeSection === 'headers' && (
                        <HeadersEditor
                            headers={endpoint.headers}
                            onChange={(headers) => onChange({ ...endpoint, headers })}
                        />
                    )}
                    
                    {activeSection === 'authorization' && (
                        <AuthorizationEditor
                            auth={endpoint.auth}
                            onChange={(auth) => onChange({ ...endpoint, auth })}
                        />
                    )}
                    
                    {activeSection === 'body' && (
                        <BodyEditor
                            body={endpoint.body}
                            onChange={(body) => onChange({ ...endpoint, body })}
                        />
                    )}
                    
                    {activeSection === 'pre-request' && (
                        <div>
                            <p className="text-sm text-gray-600 mb-2">
                                Write JavaScript code to execute before the request is sent
                            </p>
                            <CodeMirror
                                value={endpoint.pre_request_script}
                                onChange={(value) => onChange({ ...endpoint, pre_request_script: value })}
                                extensions={[javascript()]}
                                theme="light"
                                height="400px"
                            />
                        </div>
                    )}
                    
                    {activeSection === 'tests' && (
                        <div>
                            <p className="text-sm text-gray-600 mb-2">
                                Write test scripts to validate the response
                            </p>
                            <CodeMirror
                                value={endpoint.test_script}
                                onChange={(value) => onChange({ ...endpoint, test_script: value })}
                                extensions={[javascript()]}
                                theme="light"
                                height="400px"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Response Viewer Component
function ResponseViewer({ response, loading }) {
    const [activeTab, setActiveTab] = useState('body');
    
    if (loading) {
        return (
            <div className="w-1/2 bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Sending request...</p>
                </div>
            </div>
        );
    }
    
    if (!response) {
        return (
            <div className="w-1/2 bg-gray-50 flex items-center justify-center text-gray-500">
                <p>Send a request to see the response</p>
            </div>
        );
    }
    
    return (
        <div className="w-1/2 flex flex-col bg-white border-l">
            {/* Response Status */}
            <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded font-semibold ${
                            response.error || response.status_code >= 400
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                        }`}>
                            {response.status_code || 'Error'}
                        </span>
                        <span className="text-sm text-gray-600">
                            Time: {response.response_time || 0}ms
                        </span>
                        <span className="text-sm text-gray-600">
                            Size: {response.size || 0} B
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Response Tabs */}
            <div className="border-b">
                <nav className="flex">
                    {['body', 'headers', 'cookies', 'test-results'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 ${
                                activeTab === tab
                                    ? 'text-blue-600 border-blue-600'
                                    : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                        >
                            {tab === 'test-results' ? 'Test Results' : tab}
                        </button>
                    ))}
                </nav>
            </div>
            
            {/* Response Content */}
            <div className="flex-1 overflow-y-auto p-4">
                {activeTab === 'body' && (
                    <div>
                        {response.error ? (
                            <div className="text-red-600">
                                <p className="font-semibold mb-2">Error</p>
                                <pre className="text-sm">{response.message}</pre>
                            </div>
                        ) : (
                            <CodeMirror
                                value={JSON.stringify(response.body || response.data, null, 2)}
                                extensions={[json()]}
                                theme="light"
                                readOnly
                                height="100%"
                            />
                        )}
                    </div>
                )}
                
                {activeTab === 'headers' && (
                    <div className="space-y-2">
                        {Object.entries(response.headers || {}).map(([key, value]) => (
                            <div key={key} className="flex">
                                <span className="font-medium text-sm w-1/3">{key}:</span>
                                <span className="text-sm text-gray-700">{value}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {activeTab === 'test-results' && response.test_results && (
                    <div className="space-y-2">
                        {response.test_results.map((result, idx) => (
                            <div
                                key={idx}
                                className={`p-2 rounded ${
                                    result.passed ? 'bg-green-50' : 'bg-red-50'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {result.passed ? (
                                        <span className="text-green-600">✓</span>
                                    ) : (
                                        <span className="text-red-600">✗</span>
                                    )}
                                    <span className="text-sm">{result.name}</span>
                                </div>
                                {result.error && (
                                    <p className="text-xs text-red-600 mt-1">{result.error}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Save to history helper function
function saveToHistory(endpoint, response) {
    // This function saves the request to history
    console.log('Saving to history:', { endpoint, response });
}

// Main ApiTester Component
export default function ApiTester() {
    const [tabs, setTabs] = useState([]);
    const [activeTabId, setActiveTabId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [environments, setEnvironments] = useState([]);
    const [selectedEnvironment, setSelectedEnvironment] = useState(null);
    const [history, setHistory] = useState([]);
    
    // Create new tab
    const createNewTab = (endpoint = null) => {
        const newTab = {
            id: Date.now(),
            name: endpoint?.name || 'Untitled Request',
            endpoint: endpoint || {
                method: 'GET',
                url: '',
                headers: [],
                params: [],
                body: {
                    mode: 'raw',
                    raw: '',
                    formdata: [],
                    urlencoded: []
                },
                auth: {
                    type: 'none',
                    bearer: { token: '' },
                    basic: { username: '', password: '' },
                    apikey: { key: '', value: '', in: 'header' }
                },
                pre_request_script: '',
                test_script: ''
            },
            response: null,
            loading: false
        };
        
        setTabs([...tabs, newTab]);
        setActiveTabId(newTab.id);
    };
    
    // Get active tab
    const activeTab = tabs.find(t => t.id === activeTabId);
    
    // Handle search
    const handleSearch = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        
        try {
            const response = await searchEndpoints(query);
            setSearchResults(response.data.results);
        } catch (err) {
            console.error('Search error:', err);
        }
    };
    
    // Send request
    const sendRequest = async () => {
        if (!activeTab) return;
        
        const updatedTabs = tabs.map(tab => {
            if (tab.id === activeTabId) {
                return { ...tab, loading: true, response: null };
            }
            return tab;
        });
        setTabs(updatedTabs);
        
        try {
            // Replace environment variables
            const processedEndpoint = processEnvironmentVariables(activeTab.endpoint);
            
            const response = await testEndpoint({
                method: processedEndpoint.method,
                url: processedEndpoint.url,
                headers: processedEndpoint.headers,
                params: processedEndpoint.params,
                body: processedEndpoint.body,
                auth: processedEndpoint.auth,
                pre_request_script: processedEndpoint.pre_request_script,
                test_script: processedEndpoint.test_script
            });
            
            // Update tab with response
            const finalTabs = tabs.map(tab => {
                if (tab.id === activeTabId) {
                    return { 
                        ...tab, 
                        loading: false, 
                        response: response.data 
                    };
                }
                return tab;
            });
            setTabs(finalTabs);
            
            // Save to history
            saveToHistory(activeTab.endpoint, response.data);
            
        } catch (err) {
            const errorResponse = {
                error: true,
                status_code: err.response?.status || 0,
                message: err.response?.data?.detail || err.message,
                headers: err.response?.headers || {},
                data: err.response?.data || {}
            };
            
            const finalTabs = tabs.map(tab => {
                if (tab.id === activeTabId) {
                    return { 
                        ...tab, 
                        loading: false, 
                        response: errorResponse 
                    };
                }
                return tab;
            });
            setTabs(finalTabs);
        }
    };
    
    // Process environment variables
    const processEnvironmentVariables = (endpoint) => {
        if (!selectedEnvironment) return endpoint;
        
        const vars = selectedEnvironment.variables;
        let processed = JSON.stringify(endpoint);
        
        // Replace {{variable}} with actual values
        vars.forEach(v => {
            if (v.enabled) {
                const regex = new RegExp(`{{${v.key}}}`, 'g');
                processed = processed.replace(regex, v.value);
            }
        });
        
        return JSON.parse(processed);
    };
    
    return (
        <div className="h-screen flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-semibold">API Tester</h1>
                    
                    {/* Search */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search endpoints... (Ctrl+K)"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                handleSearch(e.target.value);
                            }}
                            onFocus={() => setShowSearch(true)}
                            className="px-3 py-1 border rounded-lg w-80"
                        />
                        
                        {showSearch && searchResults.length > 0 && (
                            <div className="absolute top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
                                {searchResults.map(result => (
                                    <div
                                        key={result.id}
                                        onClick={() => {
                                            createNewTab(result);
                                            setShowSearch(false);
                                            setSearchQuery('');
                                        }}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-semibold ${getMethodColor(result.method)}`}>
                                                {result.method}
                                            </span>
                                            <span className="font-medium">{result.name}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">{result.path}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Environment Selector */}
                <div className="flex items-center gap-4">
                    <select
                        value={selectedEnvironment?.id || ''}
                        onChange={(e) => {
                            const env = environments.find(env => env.id === e.target.value);
                            setSelectedEnvironment(env);
                        }}
                        className="px-3 py-1 border rounded"
                    >
                        <option value="">No Environment</option>
                        {environments.map(env => (
                            <option key={env.id} value={env.id}>{env.name}</option>
                        ))}
                    </select>
                    
                    <button
                        onClick={() => createNewTab()}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        New Request
                    </button>
                </div>
            </div>
            
            {/* Tabs */}
            <div className="bg-white border-b">
                <div className="flex items-center overflow-x-auto">
                    {tabs.map(tab => (
                        <div
                            key={tab.id}
                            className={`flex items-center gap-2 px-4 py-2 border-r cursor-pointer ${
                                activeTabId === tab.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setActiveTabId(tab.id)}
                        >
                            <span className={`text-xs font-semibold ${getMethodColor(tab.endpoint.method)}`}>
                                {tab.endpoint.method}
                            </span>
                            <span className="text-sm">{tab.name}</span>
                            {tab.loading && (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTabs(tabs.filter(t => t.id !== tab.id));
                                    if (activeTabId === tab.id) {
                                        setActiveTabId(tabs[0]?.id || null);
                                    }
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            
            {/* Main Content */}
            {activeTab ? (
                <div className="flex-1 flex">
                    <RequestBuilder
                        endpoint={activeTab.endpoint}
                        onChange={(endpoint) => {
                            const updatedTabs = tabs.map(tab => {
                                if (tab.id === activeTabId) {
                                    return { ...tab, endpoint };
                                }
                                return tab;
                            });
                            setTabs(updatedTabs);
                        }}
                        onSend={sendRequest}
                        loading={activeTab.loading}
                    />
                    
                    <ResponseViewer
                        response={activeTab.response}
                        loading={activeTab.loading}
                    />
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <p className="text-lg mb-4">No request selected</p>
                        <button
                            onClick={() => createNewTab()}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Create New Request
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}